/**
 * Error utility functions for parsing and transforming technical error messages
 * into user-friendly messages that non-technical users can understand.
 */

import { routeToModuleMap } from "@/components/Templates/ErrorBoundary/constants";

/**
 * Common error patterns to detect and transform
 */
const ERROR_PATTERNS = {
	NETWORK: /network|net::|fetch|connection|timeout|econnrefused/i,
	UNAUTHORIZED: /unauthorized|401|forbidden|403/i,
	NOT_FOUND: /not found|404/i,
	SERVER_ERROR: /server error|500|503|internal/i,
	VALIDATION: /validation|invalid|required|must be/i,
	CONFLICT: /conflict|409|already exists/i,
};

/**
 * Parses and cleans error messages to make them user-friendly
 * Removes technical jargon like error codes, status codes, stack traces
 * 
 * @param error - The error object from the API or exception
 * @param defaultMessage - The fallback message to use if parsing fails
 * @returns A clean, user-friendly error message
 */
export const parseUserFriendlyError = (
	error: any,
	defaultMessage: string,
): string => {
	let message = defaultMessage;

	// Try to extract the error message from various possible structures
	if (error?.response?.data?.error?.message) {
		// Handle nested error structures from API
		const apiError = error.response.data.error.message;
		if (typeof apiError === 'string') {
			message = apiError;
		} else if (typeof apiError === 'object' && apiError?.message) {
			message = apiError.message;
		}
	} else if (error?.response?.data?.message) {
		message = error.response.data.message;
	} else if (error?.message) {
		message = error.message;
	} else if (typeof error === 'string') {
		message = error;
	}

	// Clean up the message by removing technical patterns
	message = cleanTechnicalPatterns(message);

	// Check for common error types and provide friendly alternatives
	if (ERROR_PATTERNS.NETWORK.test(message)) {
		return 'Connection issue. Please check your internet and try again.';
	}

	if (ERROR_PATTERNS.UNAUTHORIZED.test(message)) {
		return 'You do not have permission to perform this action. Please contact your administrator.';
	}

	if (ERROR_PATTERNS.NOT_FOUND.test(message)) {
		return 'The requested information could not be found. Please refresh and try again.';
	}

	if (ERROR_PATTERNS.SERVER_ERROR.test(message)) {
		return 'A system issue occurred. Please try again later or contact support.';
	}

	// If the message is still too technical or empty, use the default
	if (!message || message.length < 5 || /^[A-Z_]+$/.test(message)) {
		return defaultMessage;
	}

	return message;
};

/**
 * Cleans technical patterns from error messages
 * Removes error codes, status codes, stack traces, etc.
 * 
 * @param message - The raw error message
 * @returns Cleaned error message
 */
const cleanTechnicalPatterns = (message: string): string => {
	if (!message) return '';

	return message
		// Remove error code patterns
		.replace(/error\s*\d+:/gi, '') // "Error 500:"
		.replace(/\[error\]/gi, '') // "[ERROR]"
		.replace(/err_\w+/gi, '') // "ERR_NETWORK"
		.replace(/code:\s*\d+/gi, '') // "code: 500"
		.replace(/status:\s*\d+/gi, '') // "status: 500"
		.replace(/status\s*code:\s*\d+/gi, '') // "status code: 500"
		// Remove HTTP method references
		.replace(/\b(GET|POST|PUT|DELETE|PATCH)\b/gi, '')
		// Remove URL patterns
		.replace(/https?:\/\/[^\s]+/gi, '')
		// Remove file paths
		.replace(/[a-z]:\\[\w\\]+/gi, '') // Windows paths
		.replace(/\/[\w/]+\.(js|ts|tsx|jsx)/gi, '') // Unix paths with extensions
		// Remove stack trace indicators
		.replace(/at\s+[\w.]+\s+\([^)]+\)/gi, '')
		.replace(/^\s*at\s+/gm, '')
		// Clean up extra whitespace
		.replace(/\s+/g, ' ')
		.trim();
};

/**
 * Helper to format validation errors in a user-friendly way
 * 
 * @param field - The field name that failed validation
 * @param message - The validation error message
 * @returns Formatted validation message
 */
export const formatValidationError = (field: string, message: string): string => {
	// Clean up field name (remove technical prefixes)
	const cleanField = field
		.replace(/^ta_/gi, '')
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase());

	return `${cleanField}: ${message}`;
};

/**
 * Helper specifically for product validation errors
 * 
 * @param productName - The name of the product
 * @param qcode - The product's qcode
 * @param context - Additional context (e.g., "selected clinic")
 * @returns User-friendly product validation message
 */
export const formatProductError = (
	productName: string,
	qcode: string,
	context: string = 'selected clinic',
): string => {
	return `The product ${productName} (${qcode}) is not available for the ${context}`;
};

export const getModuleName = (pathname: string): string => {
	// Remove query params and hash
	const cleanPath = pathname.split('?')[0].split('#')[0];
	
	// Try exact match first
	if (routeToModuleMap[cleanPath]) {
		return routeToModuleMap[cleanPath];
	}

	// Try matching parent routes (e.g., /notifications/:id -> Notifications)
	for (const [route, moduleName] of Object.entries(routeToModuleMap)) {
		if (cleanPath.startsWith(route + '/') || cleanPath === route) {
			return moduleName;
		}
	}

	return 'Unknown Module';
};
